import { lazy } from 'react';
import { Box, Text, Group, Badge, Avatar, ScrollArea, useMantineTheme, useComputedColorScheme, Tooltip, AvatarGroup } from '@mantine/core';
import { IconCalendar, IconClock, IconMapPin, IconVideo, IconUsers } from '@tabler/icons-react';
import { WidgetProps, MeetingData, UserData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// UpcomingMeetings Widget Component
// ============================================================================

function UpcomingMeetingsWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const meetings = globalData?.meetings || [];
  const users = globalData?.users || [];

  // Filter to upcoming meetings and sort by start time
  const now = new Date();
  const upcomingMeetings = meetings
    .filter((m) => new Date(m.startTime) >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    .slice(0, 6);

  const getMeetingTypeIcon = (type: MeetingData['type']) => {
    const icons = {
      meeting: IconUsers,
      call: IconVideo,
      presentation: IconCalendar,
      workshop: IconUsers,
    };
    return icons[type] || IconCalendar;
  };

  const getMeetingTypeColor = (type: MeetingData['type']) => {
    const colors = {
      meeting: 'blue',
      call: 'green',
      presentation: 'violet',
      workshop: 'orange',
    };
    return colors[type] || 'gray';
  };

  const formatTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

    return `${start.toLocaleTimeString('en-US', options)} - ${end.toLocaleTimeString('en-US', options)}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getAttendees = (attendeeIds: string[]) => {
    return attendeeIds.map((id) => users.find((u) => u.id === id)).filter(Boolean) as UserData[];
  };

  if (upcomingMeetings.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconCalendar size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No upcoming meetings
          </Text>
        </Box>
      </FadeIn>
    );
  }

  return (
    <FadeIn style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        {upcomingMeetings.map((meeting) => {
          const TypeIcon = getMeetingTypeIcon(meeting.type);
          const attendees = getAttendees(meeting.attendeeIds);
          const organizer = users.find((u) => u.id === meeting.organizerId);

          return (
            <Box
              key={meeting.id}
              p="sm"
              mb="sm"
              style={{
                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                borderRadius: theme.radius.md,
                borderLeft: `3px solid ${theme.colors[getMeetingTypeColor(meeting.type)][5]}`,
              }}
            >
              <Group justify="space-between" mb="xs" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <TypeIcon size={16} color={theme.colors[getMeetingTypeColor(meeting.type)][6]} />
                  <Text size="sm" fw={600} lineClamp={1} style={accessibilityStyles}>
                    {meeting.title}
                  </Text>
                </Group>
                <Badge size="xs" color={getMeetingTypeColor(meeting.type)} variant="light">
                  {meeting.type}
                </Badge>
              </Group>

              <Group gap="md" mb="xs">
                <Group gap={4}>
                  <IconCalendar size={12} color={theme.colors.gray[5]} />
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatDate(meeting.startTime)}
                  </Text>
                </Group>
                <Group gap={4}>
                  <IconClock size={12} color={theme.colors.gray[5]} />
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatTime(meeting.startTime, meeting.endTime)}
                  </Text>
                </Group>
              </Group>

              {meeting.location && (
                <Group gap={4} mb="xs">
                  <IconMapPin size={12} color={theme.colors.gray[5]} />
                  <Text size="xs" c="dimmed" lineClamp={1} style={accessibilityStyles}>
                    {meeting.location}
                  </Text>
                </Group>
              )}

              <Group justify="space-between" wrap="nowrap">
                {organizer && (
                  <Group gap="xs">
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      Organized by
                    </Text>
                    <Tooltip label={organizer.name} withArrow>
                      <Avatar src={organizer.avatar} size="xs" radius="xl" color="blue">
                        {organizer.name.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  </Group>
                )}

                {attendees.length > 0 && (
                  <AvatarGroup>
                    {attendees.slice(0, 3).map((attendee) => (
                      <Tooltip key={attendee.id} label={attendee.name} withArrow>
                        <Avatar src={attendee.avatar} size="xs" radius="xl" color="gray">
                          {attendee.name.charAt(0)}
                        </Avatar>
                      </Tooltip>
                    ))}
                    {attendees.length > 3 && (
                      <Avatar size="xs" radius="xl" color="gray">
                        +{attendees.length - 3}
                      </Avatar>
                    )}
                  </AvatarGroup>
                )}
              </Group>
            </Box>
          );
        })}
      </ScrollArea>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'upcoming-meetings',
  name: 'Upcoming Meetings',
  description: 'Calendar view of scheduled meetings, calls, and presentations',
  icon: IconCalendar,
  category: 'meetings',
  component: lazy(() => Promise.resolve({ default: UpcomingMeetingsWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      tall: {
        base: { cols: 12, rows: 7 },
        sm: { cols: 12, rows: 7 },
        md: { cols: 6, rows: 7 },
        lg: { cols: 4, rows: 7 },
        xl: { cols: 4, rows: 7 },
        tv: { cols: 4, rows: 6 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 8, rows: 4 },
        xl: { cols: 8, rows: 4 },
        tv: { cols: 8, rows: 4 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['meetings', 'users'],
  },
  hasSettings: true,
});

export default UpcomingMeetingsWidget;
